/**
 * ValueMark — who decided this value, at a glance (SPEC-value-mark-icon.md,
 * owner rulings 2026-10-16).
 *
 * One shape for "an AI was part of this" (✦, the app's AI glyph) and one for
 * "a person, no AI" (a person outline). The small mark on the ✦ says how:
 *
 *   human          person outline
 *   ai_approved    solid ✦ with a check       AI recommended, a person approved
 *   ai_rule        solid ✦                    AI decided, by a standing rule
 *   ai_overridden  hollow ✦ with a person     a person replaced or changed the AI's value
 *   ai_waiting     hollow ✦                   AI recommends, waiting for a person
 *
 * States differ by shape, never by color alone, so the mark reads in Paper and
 * Night and for color-blind people. The tooltip carries the words (lib/valueMark
 * builds them); it shows on hover and keyboard focus. Shapes are the owner's to
 * swap later (R-6) — they live only here.
 */
import { Tooltip } from './Indicators';
import type { ValueMarkState } from '@/lib/valueMark';

const STAR = 'M6 0.8 L7.25 4.75 L11.2 6 L7.25 7.25 L6 11.2 L4.75 7.25 L0.8 6 L4.75 4.75 Z';

const COLOR: Record<ValueMarkState, string> = {
  human: 'var(--text-ter)',
  ai_approved: 'var(--text-sec)',
  ai_rule: 'var(--text-sec)',
  ai_overridden: 'var(--text-sec)',
  ai_waiting: 'var(--mustard)',
};

function Person({ x, y, s, solid }: { x: number; y: number; s: number; solid?: boolean }) {
  // Head and shoulders in an s×s box at (x, y).
  const r = s * 0.22;
  return (
    <g fill={solid ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={solid ? 0 : 1.2} strokeLinecap="round">
      <circle cx={x + s / 2} cy={y + r + 0.6} r={r} />
      <path d={`M${x + s * 0.12} ${y + s - 0.4} C${x + s * 0.12} ${y + s * 0.6} ${x + s * 0.88} ${y + s * 0.6} ${x + s * 0.88} ${y + s - 0.4} Z`} />
    </g>
  );
}

function Glyph({ state }: { state: ValueMarkState }) {
  switch (state) {
    case 'human':
      return <Person x={2} y={1.5} s={12} />;
    case 'ai_rule':
      return <path d={STAR} transform="translate(2 2)" fill="currentColor" />;
    case 'ai_waiting':
      return <path d={STAR} transform="translate(2 2)" fill="none" stroke="currentColor" strokeWidth={1.1} strokeLinejoin="round" />;
    case 'ai_approved':
      return (
        <>
          <path d={STAR} fill="currentColor" />
          <path d="M9.6 12.6 L11.4 14.4 L15 10.6" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case 'ai_overridden':
      return (
        <>
          <path d={STAR} fill="none" stroke="currentColor" strokeWidth={1.1} strokeLinejoin="round" />
          <Person x={8.6} y={8.6} s={7.2} solid />
        </>
      );
  }
}

export interface ValueMarkProps {
  state: ValueMarkState;
  /** The words: who decided it and when (lib/valueMark). Also the accessible name. */
  tip: string;
  /** A newer AI recommendation waiting while this value stands — drawn as a second, hollow ✦. */
  waitingTip?: string | null;
  /** Icon size in px. Default 14 — no bigger than the text it sits beside. */
  size?: number;
  className?: string;
}

function Mark({ state, tip, size }: { state: ValueMarkState; tip: string; size: number }) {
  return (
    <Tooltip content={tip}>
      <span
        role="img"
        aria-label={tip}
        tabIndex={0}
        data-testid="value-mark"
        data-state={state}
        className="inline-flex shrink-0 items-center outline-none focus-visible:ring-1 focus-visible:ring-[var(--rule-bright)]"
        style={{ color: COLOR[state] }}
      >
        <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
          <Glyph state={state} />
        </svg>
      </span>
    </Tooltip>
  );
}

export function ValueMark({ state, tip, waitingTip, size = 14, className = '' }: ValueMarkProps) {
  return (
    <span className={`inline-flex items-center gap-0.5 align-middle ${className}`}>
      <Mark state={state} tip={tip} size={size} />
      {waitingTip && state !== 'ai_waiting' && <Mark state="ai_waiting" tip={waitingTip} size={size} />}
    </span>
  );
}
